import * as React from 'react';
import { Trade } from 'utils/shared/trades/types';
import type { FlowSideBasisSelection } from 'utils/shared/trades/presets';
import { isDefaultFlowSideBasisSelection } from 'utils/shared/trades/presets';
import MobileOptionCard from 'components/MobileOptionCard';
import { PRICING_PAGE, TICKER_FLOW_PAGE } from 'constants/pages';
import Link from 'components/Link';
import { RiExternalLinkLine } from 'react-icons/ri';
import PulseLoader from 'components/Loading/Pulse';
import FlowRefreshLink from 'components/FlowRefreshLink';

interface Props {
  ticker: string;
  trades: Trade[];
  isLoading: boolean;
  shouldHaveFree: boolean;
  shouldHavePaid: boolean;
  onManualRefresh?: () => void;
  flowSideBasis?: FlowSideBasisSelection;
}

const MIN_TRADES_TO_SHOW = 1;
const PROPORTION_OF_TRADES_TO_SHOW = 3;
const MAX_TRADES_TO_SHOW = 25;
const HOURS_72 = 72 * 60 * 60 * 1000;

const TickerFlowSideTable: React.FC<Props> = React.memo(
  ({
    ticker,
    trades: allTrades,
    isLoading,
    shouldHaveFree,
    shouldHavePaid,
    onManualRefresh,
    flowSideBasis,
  }) => {
    const trades = React.useMemo(() => {
      if (allTrades.length === 0) return allTrades;

      const maxDateTimestamp = new Date(allTrades[0].date).getTime();
      const filtered = allTrades.filter(
        (t) => maxDateTimestamp - new Date(t.date).getTime() <= HOURS_72,
      );

      return filtered.slice(0, MAX_TRADES_TO_SHOW);
    }, [allTrades]);

    if (isLoading) {
      return (
        <div className="flex h-full w-full flex-col items-center justify-center pb-3">
          <PulseLoader />
        </div>
      );
    }

    return (
      <div className="flex h-full flex-col overflow-y-auto">
        <div className="px-2 py-2">
          <div className="flex items-center justify-between gap-2 text-xs">
            <Link
              href={`${TICKER_FLOW_PAGE}/${ticker}${
                !flowSideBasis || isDefaultFlowSideBasisSelection(flowSideBasis)
                  ? ''
                  : `?flowSideBasis=${
                      Array.isArray(flowSideBasis) ? flowSideBasis.join(',') : flowSideBasis
                    }`
              }`}
              className="flex min-w-0 items-center gap-1.5 transition-colors hover:text-brand-primary"
              isExternal
            >
              <span className="shrink-0">Recent Trades |</span>
              <span className="truncate underline">View Full {ticker} Flow</span>
              <RiExternalLinkLine className="shrink-0 text-xs" />
            </Link>

            {shouldHavePaid ? (
              <FlowRefreshLink isAutoRefreshing className="shrink-0" />
            ) : shouldHaveFree && onManualRefresh ? (
              <FlowRefreshLink onRefresh={onManualRefresh} className="shrink-0" />
            ) : null}
          </div>
        </div>

        {shouldHaveFree && trades.length > 0 && (
          <div className="text-text-secondary mx-2 my-2 rounded border border-brand-primary/25 bg-brand-primary/10 px-2.5 py-2 text-[11px] leading-snug">
            <Link href={PRICING_PAGE} className="font-semibold text-white underline">
              Upgrade
            </Link>{' '}
            for auto-refreshing flow, real-time data, and the full feed. Free flow is delayed 30
            minutes and refreshes manually.
          </div>
        )}

        {trades.length === 0 ? (
          <div className="flex h-full w-full items-center justify-center text-gray-400">
            No trades found for {ticker}
          </div>
        ) : (
          <div className="flex-1 pb-10">
            {trades.map((trade, i) => (
              <div
                key={`${trade.ticker}-${trade.strike}-${trade.expiryString}-${i}`}
                className="relative"
              >
                <MobileOptionCard
                  trade={{
                    ...trade,
                    isBlur:
                      shouldHaveFree &&
                      i >=
                        Math.floor(
                          Math.max(
                            MIN_TRADES_TO_SHOW,
                            trades.length / PROPORTION_OF_TRADES_TO_SHOW,
                          ),
                        ),
                  }}
                  watchListTickers={[ticker]}
                  earningsMap={undefined}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  },
);

export default TickerFlowSideTable;
